import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EventTypeChannel } from '@brimo/shared';
import { api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/** Types of `docs/dev/api-appointments.md` (event types, availability, slots, booking) used by the calendar module. */

export type { EventTypeChannel };
export type EventTypeAssignment = 'single' | 'round_robin';

export interface PersonRef {
  id: string;
  name: string;
  shortName: string;
}

export interface EventTypeMember {
  userId: string;
  name: string;
  shortName: string;
  active: boolean;
}

export interface EventType {
  id: string;
  name: string;
  slug: string;
  systemKey: 'first_call' | 'consultation' | 'notary_appointment' | null;
  description: string | null;
  durationMinutes: number;
  channel: EventTypeChannel;
  channelLabel: string;
  locationText: string | null;
  assignment: EventTypeAssignment;
  assignmentLabel: string;
  owner: PersonRef | null;
  members: EventTypeMember[];
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  slotIntervalMinutes: number;
  minLeadTimeHours: number;
  bookableFromDays: number;
  bookableUntilDays: number;
  createsDeal: boolean;
  recordingEnabled: boolean;
  addressFromProperty: boolean;
  internalOnly: boolean;
  active: boolean;
  crmContactTimeline: boolean;
  crmDealTimeline: boolean;
  crmPropertyTimeline: boolean;
  hasCustomReminderChain: boolean;
  colorToken: string | null;
  bookingUrl: string | null;
  summary: string;
  tags: string[];
  stats: { bookingsLast30Days: number };
  canEdit: boolean;
}

export interface EventTypeInput {
  name: string;
  slug?: string;
  description?: string | null;
  durationMinutes: number;
  channel: EventTypeChannel;
  locationText?: string | null;
  assignment?: EventTypeAssignment;
  ownerId?: string | null;
  members?: Array<{ userId: string; active?: boolean }>;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  slotIntervalMinutes?: number;
  minLeadTimeHours?: number;
  bookableFromDays?: number;
  bookableUntilDays?: number;
  createsDeal?: boolean;
  recordingEnabled?: boolean;
  addressFromProperty?: boolean;
  internalOnly?: boolean;
  active?: boolean;
  crmContactTimeline?: boolean;
  crmDealTimeline?: boolean;
  crmPropertyTimeline?: boolean;
  colorToken?: string | null;
}

export interface AvailabilityWindow {
  start: string;
  end: string;
}

export interface AvailabilityDay {
  weekday: number;
  label: string;
  enabled: boolean;
  windows: AvailabilityWindow[];
}

export interface Availability {
  userId: string;
  timezone: string;
  days: AvailabilityDay[];
}

export interface Slot {
  startsAt: string;
  endsAt: string;
  advisorId: string;
}

export interface SlotsResponse {
  eventTypeId: string;
  durationMinutes: number;
  days: Array<{ date: string; slots: Slot[] }>;
}

export interface AppointmentCard {
  id: string;
  eventType: { id: string; name: string; systemKey: string | null; colorToken: string | null };
  status: 'booked' | 'rescheduled' | 'cancelled' | 'attended' | 'no_show';
  statusLabel: string;
  pill: string;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  timeRangeText: string;
  channel: EventTypeChannel;
  channelLabel: string;
  location: string | null;
  advisor: PersonRef;
  setter: PersonRef | null;
  contact: { id: string; firstName: string; lastName: string; name: string } | null;
  guestName: string | null;
  deal: { id: string; number: string | number } | null;
  bookedVia: string;
  bookedViaLabel: string;
  outcome: string | null;
  outcomeText: string | null;
  conflictOverridden: boolean;
  createdAt: string;
}

export interface CreateAppointmentBody {
  eventTypeId: string;
  contactId: string;
  advisorId?: string;
  startsAt: string;
  durationMinutes?: number;
  channel?: EventTypeChannel;
  location?: string;
  dealId?: string;
  ticketId?: string;
  concern?: string;
  setterId?: string;
  completesTaskId?: string;
  overrideConflict?: boolean;
}

export interface ConflictEntry {
  kind: 'appointment' | 'external' | 'hold';
  title: string;
  startsAt: string;
  endsAt: string;
  overlapMinutes: number;
}

/** `details` of the 409 „Terminkonflikt" answer of `POST /appointments`. */
export interface ConflictDetails {
  conflicts: ConflictEntry[];
  alternatives: Slot[];
  dayOverview: Array<Omit<ConflictEntry, 'overlapMinutes'>>;
  requested: { startsAt: string; endsAt: string; advisorId: string };
}

export function readConflictDetails(details: Record<string, unknown> | undefined): ConflictDetails | null {
  if (!details) return null;
  const conflicts = details['conflicts'];
  const alternatives = details['alternatives'];
  const dayOverview = details['dayOverview'];
  const requested = details['requested'];
  if (!Array.isArray(conflicts) || !Array.isArray(alternatives) || !Array.isArray(dayOverview)) return null;
  if (typeof requested !== 'object' || requested === null) return null;
  return {
    conflicts: conflicts as ConflictEntry[],
    alternatives: alternatives as Slot[],
    dayOverview: dayOverview as ConflictDetails['dayOverview'],
    requested: requested as ConflictDetails['requested'],
  };
}

// --- contacts (the participant picker of Beratung 02 uses the contacts list and record) ------------------

export interface ContactSearchItem {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  lifecycle: { key: string; label: string; tone: string };
  deal: string | null;
  owner: { id: string; name: string } | null;
  dnc: boolean;
}

export interface ContactLinkedItem {
  id: string;
  title: string;
  meta: string | null;
  status: { label: string; tone: string } | null;
  value: string | null;
  href: string | null;
}

export interface ContactRecordSummary {
  id: string;
  header: {
    name: string;
    firstName: string;
    lastName: string;
    initials: string;
    email: string | null;
    phone: string | null;
    dnc: boolean;
  };
  tools: Record<string, { enabled: boolean; reason: string | null }>;
  linked: Array<{ key: string; label: string; items: ContactLinkedItem[]; emptyText: string }>;
}

// --- queries ------------------------------------------------------------------------------------------------

export const eventTypesKey = [...queryRoots.eventTypes] as const;

export function useEventTypes() {
  return useQuery({
    queryKey: eventTypesKey,
    queryFn: () => api<{ items: EventType[] }>('/event-types'),
    select: (data) => data.items,
  });
}

export function useEventType(id: string | undefined) {
  return useQuery({
    queryKey: [...queryRoots.eventTypes, id ?? 'none'],
    queryFn: () => api<EventType>(`/event-types/${id ?? ''}`),
    enabled: id !== undefined,
  });
}

export function useSaveEventType(id: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<EventTypeInput>) =>
      id === undefined
        ? api<EventType>('/event-types', { method: 'POST', body: input })
        : api<EventType>(`/event-types/${id}`, { method: 'PATCH', body: input }),
    onSuccess: async (saved) => {
      queryClient.setQueryData([...queryRoots.eventTypes, saved.id], saved);
      await queryClient.invalidateQueries({ queryKey: eventTypesKey });
    },
  });
}

export function useDeleteEventType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/event-types/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: eventTypesKey });
    },
  });
}

export function useAvailability(userId?: string) {
  return useQuery({
    queryKey: [...queryRoots.availability, userId ?? 'me'],
    queryFn: () => api<Availability>(userId ? `/availability?userId=${userId}` : '/availability'),
  });
}

export function useSaveAvailability(userId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (days: Array<Pick<AvailabilityDay, 'weekday' | 'enabled' | 'windows'>>) =>
      api<Availability>('/availability', {
        method: 'PUT',
        body: userId ? { userId, days } : { days },
      }),
    onSuccess: async (saved) => {
      queryClient.setQueryData([...queryRoots.availability, userId ?? 'me'], saved);
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.slots] });
    },
  });
}

export interface SlotsParams {
  eventTypeId: string;
  from: string;
  to: string;
  advisorId?: string | undefined;
  internal?: boolean | undefined;
}

export function slotsPath(params: SlotsParams): string {
  const search = new URLSearchParams({ eventTypeId: params.eventTypeId, from: params.from, to: params.to });
  if (params.advisorId) search.set('advisorId', params.advisorId);
  if (params.internal) search.set('internal', '1');
  return `/slots?${search.toString()}`;
}

export function useSlots(params: SlotsParams | null) {
  return useQuery({
    queryKey: [...queryRoots.slots, params],
    queryFn: () => api<SlotsResponse>(slotsPath(params as SlotsParams)),
    enabled: params !== null,
    staleTime: 30_000,
  });
}

export function useContactSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: [...queryRoots.contacts, 'search', q],
    queryFn: () =>
      api<{ items: ContactSearchItem[]; total: number }>(`/contacts?q=${encodeURIComponent(q)}&limit=8`),
    enabled: q.length > 0,
    staleTime: 15_000,
  });
}

export function useContactRecord(id: string | null) {
  return useQuery({
    queryKey: [...queryRoots.contact, id ?? 'none'],
    queryFn: () => api<ContactRecordSummary>(`/contacts/${id ?? ''}`),
    enabled: id !== null,
  });
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAppointmentBody) =>
      api<AppointmentCard & { contactEmail: string | null }>('/appointments', { method: 'POST', body }),
    onSuccess: async (_created, body) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...queryRoots.appointments] }),
        queryClient.invalidateQueries({ queryKey: [...queryRoots.slots] }),
        queryClient.invalidateQueries({ queryKey: [...queryRoots.contact, body.contactId] }),
        queryClient.invalidateQueries({ queryKey: [...queryRoots.contacts] }),
      ]);
    },
  });
}
